import React from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface UserProfilePreviewProps {
  profile: {
    id: string;
    contact_name: string;
    email?: string;
    phone?: string;
    whatsapp?: string;
    bio?: string;
    image_url?: string;
    role?: string;
  };
}

export const UserProfilePreview: React.FC<UserProfilePreviewProps> = ({ profile }) => {
  const { t } = useLanguage();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="theme-card overflow-hidden">
        {/* Profile Header */}
        <div className="relative bg-gradient-to-r from-slate-800 to-slate-700 p-6 pb-16">
          <div className="absolute bottom-0 left-6 transform translate-y-1/2">
            <div className="relative">
              {profile.image_url ? (
                <img 
                  src={profile.image_url} 
                  alt={profile.contact_name} 
                  className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-white border-4 border-white shadow-lg flex items-center justify-center">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end">
            <span className="inline-block px-3 py-1 bg-primary-200 text-primary-800 rounded-full text-sm font-medium">
              {t(profile.role ? profile.role.toLowerCase() : 'user')}
            </span>
          </div>
        </div>
  
        {/* Profile Content */}
        <div className="px-6 pt-20 pb-8 space-y-8">
          {/* Name and Basic Info */}
          <div className="space-y-1">
            <h1 className="text-3xl font-bold text-gray-900">
              {profile.contact_name}
            </h1>
          </div>
  
          {/* Bio Section */}
          {profile.bio && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                {t('about')}
              </h3>
              <p className="text-gray-700 leading-relaxed max-w-2xl">
                {profile.bio}
              </p>
            </div>
          )}
  
          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              {t('contactInfo')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {profile.email && (
                <div className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0 p-3 bg-white rounded-md shadow-sm">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('email')}</p>
                    <p className="text-gray-900">{profile.email}</p>
                  </div>
                </div>
              )}
  
              {profile.phone && (
                <div className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0 p-3 bg-white rounded-md shadow-sm">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{t('phone')}</p>
                    <p className="text-gray-900">{profile.phone}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
  
          {/* Contact Options */}
          <div className="flex flex-wrap justify-end gap-2">
            {/* <Link
              to={`/chat/${profile.id}`}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
            >
              {t('chat')}
            </Link> */}
            {profile.phone && (
              <a
                href={`tel:${profile.phone}`}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-md"
              >
                {t('call')}
              </a>
            )}
            {profile.whatsapp && (
              <a
                href={`https://wa.me/${profile.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
              >
                {t('whatsapp')}
              </a>
            )}
            {/* Add additional direct links for telegram, messenger, etc. if available */}
          </div>
        </div>
      </div>
    </div>
  );
};